import type { SwUpdate } from '@angular/service-worker';

const BUTTON_CLASS = 'app-update-button';
const IDLE_LABEL = 'Aggiorna app';
let updateInProgress = false;

type UpdateButtonState = 'idle' | 'checking' | 'current' | 'ready' | 'unavailable' | 'error';

function updateButtons(label: string, disabled: boolean, state: UpdateButtonState): void {
  for (const button of document.querySelectorAll<HTMLButtonElement>(`.${BUTTON_CLASS}`)) {
    button.disabled = disabled;
    button.setAttribute('data-update-state', state);
    const text = button.querySelector<HTMLElement>('.app-update-label');
    if (text) text.textContent = label;
  }
}

async function requestUpdate(updates: SwUpdate): Promise<void> {
  if (updateInProgress) return;
  updateInProgress = true;
  updateButtons('Controllo aggiornamenti…', true, 'checking');

  try {
    if (!updates.isEnabled) {
      updateInProgress = false;
      updateButtons('Aggiornamento PWA non disponibile', false, 'unavailable');
      return;
    }

    const updateReady = await updates.checkForUpdate();
    if (updateReady) {
      updateButtons('Nuova versione pronta · ricarico…', true, 'ready');
      // Angular recommends reloading the client once the new version is ready,
      // rather than hot-activating a potentially mismatched app shell/chunk set.
      window.location.reload();
      return;
    }

    updateInProgress = false;
    updateButtons('App già aggiornata ✓', false, 'current');
  } catch (error) {
    console.warn('Unable to check for a Learning Garden update.', error);
    updateInProgress = false;
    updateButtons('Controllo fallito · riprova', false, 'error');
  }
}

function createButton(updates: SwUpdate): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = BUTTON_CLASS;
  button.setAttribute('data-update-state', 'idle');
  button.innerHTML = `<span class="app-update-icon" aria-hidden="true">↻</span> <span class="app-update-label">${IDLE_LABEL}</span>`;
  button.setAttribute('aria-label', 'Controlla e carica la versione più recente dell’app');
  button.addEventListener('click', () => void requestUpdate(updates));
  return button;
}

function attachButtons(updates: SwUpdate): void {
  for (const actions of document.querySelectorAll<HTMLElement>('.adult-tools-actions')) {
    if (actions.querySelector(`.${BUTTON_CLASS}`)) continue;
    const button = createButton(updates);
    button.disabled = updateInProgress;
    if (updateInProgress) {
      button.setAttribute('data-update-state', 'checking');
      const text = button.querySelector<HTMLElement>('.app-update-label');
      if (text) text.textContent = 'Controllo aggiornamenti…';
    }
    actions.appendChild(button);
  }
}

export function installAppUpdateControl(updates: SwUpdate): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  let scheduled = false;
  const schedule = (): void => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      attachButtons(updates);
    });
  };

  const observer = new MutationObserver(schedule);
  observer.observe(document.body, { childList: true, subtree: true });
  schedule();
}
